import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import logo from "@/assets/logo.png";
import { api } from "@/lib/api";
import { setAuth } from "@/lib/auth";

export default function LoginPage() {
  const navigate = useNavigate();

  const PHOTOS = [
    "/Foto-68.JPEG",
    "/Foto-152.JPEG",
    "/Foto-138.JPEG",
  ];

  const [photoIdx, setPhotoIdx] = useState(() =>
    Math.floor(Math.random() * PHOTOS.length)
  );

  useEffect(() => {
    const t = setInterval(() => {
      setPhotoIdx((i) => (i + 1) % PHOTOS.length);
    }, 10_000);
    return () => clearInterval(t);
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.auth.login.$post({ json: { email: email.trim().toLowerCase(), password } });
      const data = await res.json();
      if ("error" in data) {
        setError(data.error || "No se pudo iniciar sesión.");
        return;
      }
      const { isSuperAdmin: sa, ...userRest } = data.user;
      const user = sa ? { ...userRest, isSuperAdmin: true as const } : userRest;
      setAuth({ token: data.token, csrfToken: data.csrfToken, user });
      Sentry.setUser({ id: user.id, email: user.email, username: `${user.firstName} ${user.lastName}` });
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const status = err instanceof Error && err.cause instanceof Response
        ? err.cause.status
        : null;

      if (status === 401) {
        setError("El email o la contraseña no son correctos.");
      } else if (status === 429) {
        setError("Demasiados intentos. Esperá unos minutos y probá nuevamente.");
      } else if (status === null) {
        setError("No se pudo conectar con el servidor. Verificá tu conexión e intentá nuevamente.");
      } else {
        setError("No se pudo iniciar sesión. Intentá nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              <div className="flex flex-col items-center gap-4 text-center">
                <img src={logo} alt="Logo" className="h-12 w-auto" />
                <div className="flex flex-col items-center gap-2">
                  <h1 className="text-2xl font-bold">Iniciar sesión</h1>
                  <p className="text-muted-foreground text-sm text-balance">
                    Sistema de Central de Control y Reconciliación
                  </p>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-gray-300 placeholder:text-gray-400"
                  />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Contraseña</Label>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border-gray-300 placeholder:text-gray-400"
                  />
                </div>

                {error && (
                  <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-center text-sm text-red-700 dark:bg-red-950/30 dark:text-red-400">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-black text-white hover:bg-zinc-800 h-11 rounded-lg mt-2"
                >
                  {loading ? "Ingresando..." : "Ingresar"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="bg-muted relative hidden lg:block overflow-hidden">
        {PHOTOS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 dark:brightness-[0.2] dark:grayscale ${
              i === photoIdx ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
